export const FUNDAMENTAL_OPTIONS = [
  { value: "eps", label: "Earnings Per Share" },
  { value: "shares-outstanding", label: "Shares Outstanding" },
  { value: "revenues", label: "Revenue" },
  { value: "net_incomes", label: "Net Income" },
  { value: "fcf", label: "Free Cash Flow" },
  { value: "insider", label: "Insider Transactions" },
  { value: "volume", label: "Volume" },
];

const SUPPORTED_CHART_SCALES = new Set([
  "15s",
  "30s",
  "1m",
  "2m",
  "3m",
  "5m",
  "10m",
  "15m",
  "30m",
  "1h",
  "2h",
  "3h",
  "4h",
  "1d",
  "1w",
  "1mo",
]);

export const normalizeSupportedScale = (scale, fallback = "1d") => {
  const normalized = {
    "1day": "1d",
    "1min": "1m",
    "2min": "2m",
    "3min": "3m",
    "5min": "5m",
  }[String(scale || "")] || String(scale || "");

  return SUPPORTED_CHART_SCALES.has(normalized) ? normalized : fallback;
};

export const normalizeInlineTickerPrices = (prices) => {
  if (!Array.isArray(prices)) return [];

  return prices
    .map((entry) => {
      const date = entry?.datetime || entry?.date;
      const close = Number(entry?.close);
      if (!date || !Number.isFinite(close)) {
        return null;
      }

      // Older historical bars can be partially populated. Keep valid close
      // rows renderable instead of truncating long-horizon chart history.
      const openRaw = Number(entry?.open);
      const highRaw = Number(entry?.high);
      const lowRaw = Number(entry?.low);
      const open = Number.isFinite(openRaw) ? openRaw : close;
      const high = Number.isFinite(highRaw) ? highRaw : close;
      const low = Number.isFinite(lowRaw) ? lowRaw : close;
      const volume = Number(entry?.volume);
      const vwap = Number(entry?.vwap);
      return {
        datetime: date,
        date,
        open,
        high,
        low,
        close,
        volume: Number.isFinite(volume) ? volume : 0,
        vwap: Number.isFinite(vwap) ? vwap : null,
      };
    })
    .filter(Boolean);
};

const comparableBarTime = (bar) => {
  const raw = bar?.datetime || bar?.date;
  if (!raw) return null;

  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : String(raw);
};

const comparableBarNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const sameBarValue = (left, right) => {
  const leftNum = comparableBarNumber(left);
  const rightNum = comparableBarNumber(right);
  if (leftNum == null && rightNum == null) return true;
  if (leftNum == null || rightNum == null) return false;
  return Math.abs(leftNum - rightNum) <= 0.0000001;
};

const sameLatestDailyBar = (left, right) => (
  comparableBarTime(left) === comparableBarTime(right) &&
  sameBarValue(left?.open, right?.open) &&
  sameBarValue(left?.high, right?.high) &&
  sameBarValue(left?.low, right?.low) &&
  sameBarValue(left?.close, right?.close) &&
  sameBarValue(left?.volume, right?.volume)
);

export const shouldReplaceInlineTickerPrices = (existingPrices, nextPrices) => {
  if (!Array.isArray(nextPrices) || nextPrices.length === 0) return false;
  if (!Array.isArray(existingPrices) || existingPrices.length === 0) return true;

  const existingLatest = existingPrices[existingPrices.length - 1];
  const nextLatest = nextPrices[nextPrices.length - 1];
  const existingTime = comparableBarTime(existingLatest);
  const nextTime = comparableBarTime(nextLatest);

  if (typeof existingTime === "number" && typeof nextTime === "number") {
    if (nextTime > existingTime) return true;
    if (nextTime < existingTime) return false;
  } else if (existingTime !== nextTime) {
    return String(nextTime || "") > String(existingTime || "");
  }

  if (!sameLatestDailyBar(existingLatest, nextLatest)) return true;

  return nextPrices.length > existingPrices.length;
};

// A message's inline daily series (ticker_prices) is built for its first
// answered ticker only (Chat::FinalResponseChartPayload). Keying it by the
// selected ticker showed the first ticker's candles under every other ticker
// in a multi-ticker chart (production, 2026-09-27: ALAB showed CRDO's bars).
export const inlineDailyChartKeyFor = ({
  answeredTickerIds,
  selectedTickerId,
  scale,
  idx,
  hasInlinePrices,
}) => {
  if (!hasInlinePrices || selectedTickerId == null) return null;
  if (normalizeSupportedScale(scale) !== "1d") return null;
  const ownerId = Array.isArray(answeredTickerIds) ? answeredTickerIds[0] : null;
  if (ownerId == null || String(ownerId) !== String(selectedTickerId)) return null;
  return `${String(selectedTickerId)}-${scale}-${idx}`;
};

export const resolveTickerAutoSelection = ({
  answeredTickerIds,
  selectedTickerId,
  userTickerOverride = false,
}) => {
  const tickerIds = Array.isArray(answeredTickerIds)
    ? answeredTickerIds.filter((id) => id != null).map((id) => String(id))
    : [];
  const selected = selectedTickerId == null ? null : String(selectedTickerId);
  const firstTickerId = tickerIds[0] || null;

  if (!firstTickerId) {
    return {
      nextTickerId: null,
      shouldUpdate: selected != null,
      userTickerOverride: false,
    };
  }

  if (userTickerOverride && selected && tickerIds.includes(selected)) {
    return {
      nextTickerId: selected,
      shouldUpdate: false,
      userTickerOverride: true,
    };
  }

  return {
    nextTickerId: firstTickerId,
    shouldUpdate: selected !== firstTickerId,
    userTickerOverride: false,
  };
};

export const parseTradeNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const cleaned = String(value).replace(/,/g, "").trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

export const getTradeProposalFromThread = (thread) => {
  if (!thread || typeof thread !== "object") return null;
  const refinedQueryObj =
    thread.refinedQueryObj && typeof thread.refinedQueryObj === "object"
      ? thread.refinedQueryObj
      : null;
  if (!refinedQueryObj) return null;
  const proposal =
    refinedQueryObj.trade_proposed || refinedQueryObj.tradeProposed || null;
  return proposal && typeof proposal === "object" ? proposal : null;
};

export const getAgenticChartStateFromThread = (thread) => {
  if (!thread || typeof thread !== "object") return null;
  if (thread.agenticChartState && typeof thread.agenticChartState === "object") {
    return thread.agenticChartState;
  }
  if (thread.agentic_chart_state && typeof thread.agentic_chart_state === "object") {
    return thread.agentic_chart_state;
  }
  const refinedQueryObj =
    thread.refinedQueryObj && typeof thread.refinedQueryObj === "object"
      ? thread.refinedQueryObj
      : null;
  const state =
    refinedQueryObj?.agentic_chart_state || refinedQueryObj?.agenticChartState;
  return state && typeof state === "object" ? state : null;
};

export const getScaleForTradeDuration = (rawDuration) => {
  const duration = String(rawDuration ?? "").trim().toLowerCase();
  if (!duration) return "1d";
  if (/\b(minutes?|hours?)\b/.test(duration)) return "15m";
  if (/\bdays?\b/.test(duration)) return "1h";
  if (/\b(months?|years?)\b/.test(duration)) return "1w";
  return "1d";
};

export const getPreferredScaleFromMessageThread = (thread) => {
  const agenticChartState = getAgenticChartStateFromThread(thread);
  if (agenticChartState?.scale) {
    const normalizedScale = normalizeSupportedScale(agenticChartState.scale, null);
    if (normalizedScale) return normalizedScale;
  }

  const tradeProposal = getTradeProposalFromThread(thread);
  if (!tradeProposal) return "1d";
  const duration =
    tradeProposal.expected_trade_duration ??
    tradeProposal.expectedTradeDuration ??
    tradeProposal["expected-trade-duration"] ??
    "";
  return normalizeSupportedScale(getScaleForTradeDuration(duration));
};
